import { fetchWithDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { getBetterAuthBaseUrl } from "#auth-client";
import {
  betterAuthSocialProviderAvailabilitySchema,
  type BetterAuthSocialProviderAvailability,
} from "#better-auth-social-provider-availability";

const SOCIAL_PROVIDERS_PATH = "/api/auth/social-providers";
const TRAILING_SLASH_RE = /\/$/;

/** Shared by the form and the drawer, which warms these controls before sign-in opens. */
export const betterAuthSocialProviderAvailabilityQueryOptions = {
  queryFn: () => fetchBetterAuthSocialProviderAvailability(),
  queryKey: ["better-auth", "social-providers"],
  staleTime: Number.POSITIVE_INFINITY,
};

export async function fetchBetterAuthSocialProviderAvailability(
  baseURL?: string,
): Promise<BetterAuthSocialProviderAvailability> {
  // Use the SAME resolver as the Better Auth client (`getBetterAuthBaseUrl`),
  // NOT the env-only `resolveBetterAuthBaseUrl`. On the Android emulator a dev
  // build inlines `EXPO_PUBLIC_BETTER_AUTH_URL=http://127.0.0.1:4574`, which the
  // env-only resolver returns verbatim — but `127.0.0.1` is the emulator itself,
  // so this fetch dies with ConnectException, the availability query errors, and
  // `googleOAuthEnabled` stays false → the "Sign in with Google" button never
  // renders on Android. `getBetterAuthBaseUrl` derives the API origin from the
  // dev-client's actual `hostUri` (the host the bundle loaded from), which is
  // reachable on every platform.
  const origin = (baseURL ?? getBetterAuthBaseUrl()).replace(TRAILING_SLASH_RE, "");
  // Bounded (headers and body) so the sign-in controls this gates can never wait on it forever.
  const response = await fetchWithDeadline(`${origin}${SOCIAL_PROVIDERS_PATH}`, {
    operation: "Better Auth social provider availability",
  });
  if (!response.ok) {
    throw new Error(`Failed to load Better Auth social providers (${response.status})`);
  }
  return betterAuthSocialProviderAvailabilitySchema.parse(await response.json());
}
